import * as moment from 'moment-timezone';
import { Status } from '@core/enums/status.enum';

// Must match the default {days} of the api's runDeactivateInactiveOperators job.
export const INACTIVE_OPERATOR_CLEANUP_DAYS = 60;

// Must match the api's config/operator_cleanup.php: display_timezone, super_admin_role_id and exempt_types.
const CLEANUP_TIMEZONE = 'Asia/Kuala_Lumpur';
const SUPER_ADMIN_ROLE_ID = 1;
const EXEMPT_TYPES = [1, 2, 3, 4]; // admin, agent, bot, AI chatbot

// The real run, scheduled weekdays()->dailyAt('22:00') in GMT+8 in the api's Console Kernel:
// Monday to Friday only.
const CLEANUP_RUN_HOUR = 22;

export interface CleanupOperator {
  application_role_id?: number | null;
  type?: number;
  support?: number;
  last_login?: string | Date | null;
  created_at?: string | Date | null;
}

export interface CleanupForm {
  status: number | string;
  password: string | null;
  application_role_id: number | string | (number | string)[] | null;
}

/**
 * True when the cleanup job would list this operator on its next 22:00 GMT+8 run: their last
 * successful login, or creation if they never logged in, is at or before midnight GMT+8 on that
 * run's day minus `days`. From 22:00 the next run is the next day's, and there is no run on
 * Saturday or Sunday, so from Friday 22:00 until Monday 22:00 the next run is Monday's. Listed is
 * not always deleted: the delete switch can be off, and the login-record guard can stop a run. The
 * job also skips two cases BO cannot see: anyone holding a live token, and the Odin service
 * account (by username, when odin.service_username is set).
 *
 * Expects the row from GET /operator/{id} (Operator::showData): last_login 'YYYY-MM-DD HH:mm:ss'
 * in UTC, created_at ISO 8601. A list row's last_login ('... UTC+08:00') does not parse, so the
 * function returns false rather than guess.
 */
export function isInactiveOperatorCleanupCandidate(operator: CleanupOperator, now: Date, days = INACTIVE_OPERATOR_CLEANUP_DAYS): boolean {
  if (!operator || operator.application_role_id == null || +operator.application_role_id === SUPER_ADMIN_ROLE_ID) {
    return false;
  }

  if (EXEMPT_TYPES.includes(+operator.type) || +operator.support === 1) {
    return false;
  }

  const lastActivity = operator.last_login || operator.created_at;
  if (!lastActivity) {
    return false;
  }

  const lastActivityAt = moment.utc(lastActivity, moment.ISO_8601, true);
  if (!lastActivityAt.isValid()) {
    return false;
  }

  const runDay = moment(now).tz(CLEANUP_TIMEZONE);
  if (runDay.hour() >= CLEANUP_RUN_HOUR) {
    runDay.add(1, 'day');
  }
  while (runDay.isoWeekday() > 5) {
    runDay.add(1, 'day');
  }
  const cutoff = runDay.startOf('day').subtract(days, 'days');

  return !lastActivityAt.isAfter(cutoff);
}

/**
 * Where the operator edit dialog shows the cleanup warning: under status when the edit turns an
 * Inactive operator Active, else under password when a new one is typed. Null when neither, or
 * when the operator, with the role chosen in the form, would not be listed. The date check runs
 * only once one of those edits is made.
 */
export function inactiveOperatorCleanupWarningField(saved: CleanupOperator & { status?: number }, form: CleanupForm, now: Date): 'status' | 'password' | null {
  let field: 'status' | 'password' | null = null;
  if (+saved.status === Status.Inactive && +form.status === Status.Active) {
    field = 'status';
  } else if (form.password) {
    field = 'password';
  }
  if (!field) {
    return null;
  }

  const roleId = Array.isArray(form.application_role_id) ? form.application_role_id[0] : form.application_role_id;
  const operator = { ...saved, application_role_id: roleId === null || roleId === undefined || roleId === '' ? null : Number(roleId) };

  return isInactiveOperatorCleanupCandidate(operator, now) ? field : null;
}
